// src/pages/pengamatan/berat-tanaman/LayoutBeratTanamanSAP.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Stack,
    Snackbar,
    TextField,
    Autocomplete,
    IconButton,
    Chip,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ClearIcon from '@mui/icons-material/Clear';

import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import XLSX untuk export
import * as XLSX from 'xlsx';

// Import formatters
import {
    formatText
} from "../../../utils/formatters-master";

// Import komponen
import CustomTableContainer from "../../../components/CustomScrollContainer";
import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

function LayoutBeratTanamanSAP() {
    const { user, loading: authLoading } = useAuthCheck();
    const { breadcrumbs } = useOutletContext();

    const [searchOptions, setSearchOptions] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedLokasi, setSelectedLokasi] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    // State untuk menyimpan semua data dari Firebase
    const [allData, setAllData] = useState({});
    // State untuk menyimpan data plot yang akan ditampilkan
    const [plotData, setPlotData] = useState([]);
    // State untuk menyimpan detail lokasi yang dipilih
    const [lokasiDetail, setLokasiDetail] = useState(null);

    // Fetch semua data sekaligus
    useEffect(() => {
        if (!user) return;

        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                const pengamatanRef = ref(database, "db_freshpine/tabel_catatan_pengamatan/pengamatan_berat_tanaman/lokasi_pengamatan");
                
                const unsubscribe = onValue(pengamatanRef, (snapshot) => {
                    const data = snapshot.val();
                    
                    if (data) {
                        // Simpan semua data
                        setAllData(data);
                        
                        // Buat daftar lokasi unik untuk dropdown
                        const lokasiSet = new Set();
                        const lokasiOptions = [];
                        const lokasiDetails = {};
                        
                        Object.keys(data).forEach(lokasiKey => {
                            const lokasi = data[lokasiKey];
                            
                            if (lokasi && lokasi.status_pengamatan === "Complete" && lokasi.kode_lokasi) {
                                const kodeLokasi = lokasi.kode_lokasi;
                                
                                if (!lokasiSet.has(kodeLokasi)) {
                                    lokasiSet.add(kodeLokasi);
                                    lokasiOptions.push(kodeLokasi);
                                    lokasiDetails[kodeLokasi] = {
                                        firebaseKey: lokasiKey,
                                        tanggal_pengamatan: lokasi.tanggal_pengamatan || '',
                                        fmp: lokasi.fmp || '',
                                        id_mandor: lokasi.id_mandor || '',
                                        user_uid: lokasi.user_uid || ''
                                    };
                                }
                            }
                        });
                        
                        // Urutkan lokasi
                        lokasiOptions.sort();
                        setSearchOptions(lokasiOptions);
                        
                        // Simpan detail lokasi di state
                        setAllData(prev => ({
                            ...prev,
                            _lokasiDetails: lokasiDetails
                        }));
                    }
                    
                    setLoadingData(false);
                }, (error) => {
                    console.error("Error fetching data:", error);
                    setSnackbar({
                        open: true,
                        message: "Gagal memuat data pengamatan berat tanaman.",
                        severity: "error",
                    });
                    setLoadingData(false);
                });

                return () => unsubscribe();
            } catch (error) {
                console.error("Error setting up data fetch:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
                setLoadingData(false);
            }
        };

        fetchAllData();
    }, [user]);

    // Ketika lokasi dipilih, ambil data plotnya
    useEffect(() => {
        if (!selectedLokasi) {
            setPlotData([]);
            setLokasiDetail(null);
            return;
        }

        if (!allData._lokasiDetails) {
            setPlotData([]);
            setLokasiDetail(null);
            return;
        }

        const lokasiDetailFromData = allData._lokasiDetails[selectedLokasi];
        if (!lokasiDetailFromData) {
            setPlotData([]);
            setLokasiDetail(null);
            return;
        }

        // Simpan detail lokasi
        setLokasiDetail(lokasiDetailFromData);

        // Cari data plot dari allData
        const lokasiKey = lokasiDetailFromData.firebaseKey;
        const lokasiData = allData[lokasiKey];
        
        if (!lokasiData || !lokasiData.plot_pengamatan) {
            setPlotData([]);
            return;
        }

        const plotDataRaw = lokasiData.plot_pengamatan;
        const plotArray = [];
        
        Object.keys(plotDataRaw).forEach(plotKey => {
            const plot = plotDataRaw[plotKey];
            
            // Ambil data sebaran tanaman sample
            const sebaranData = plot.inputan_data_pengamatan?.sebaran_tanaman_sample || {};
            
            // Ambil data berat tanaman dari output_data_pengamatan
            const beratData = plot.output_data_pengamatan?.berat_tanaman_kg || {};
            
            // Ambil data warna daun dari inputan_data_pengamatan
            const warnaDaunData = plot.inputan_data_pengamatan?.warna_daun || {};
            
            // Ambil data tanaman abnormal dari inputan_data_pengamatan
            const tanamanAbnormalData = plot.inputan_data_pengamatan?.tanaman_abnormal || {};
            
            // Hitung jumlah untuk Lain-lain (mati + lain-lain)
            const mati = parseInt(tanamanAbnormalData.mati || '0');
            const lainLain = parseInt(tanamanAbnormalData['lain-lain'] || '0');
            const totalLainLain = mati + lainLain;
            
            // Ambil data kerdil
            const kerdil = tanamanAbnormalData.kerdil || '0';
            
            plotArray.push({
                plot: plot.plot || '',
                pjp: plot.pjp || '',
                luas_plot: plot.luas_plot || '',
                plotKey: plotKey,
                
                // Data sebaran tanaman sample
                sebaran_sk: sebaranData.sk || '0',
                sebaran_sk_a: sebaranData['sk-a'] || '0',
                sebaran_k: sebaranData.k || '0',
                sebaran_k_a: sebaranData['k-a'] || '0',
                sebaran_s: sebaranData.s || '0',
                sebaran_b_a: sebaranData['b-a'] || '0',
                sebaran_b: sebaranData.b || '0',
                sebaran_sb_a: sebaranData['sb-a'] || '0',
                sebaran_sb: sebaranData.sb || '0',
                
                // Data berat tanaman destruktif (Kg)
                berat_sk: beratData.sk || '0',
                berat_sk_a: beratData['sk-a'] || '0',
                berat_k: beratData.k || '0',
                berat_k_a: beratData['k-a'] || '0',
                berat_s: beratData.s || '0',
                berat_b_a: beratData['b-a'] || '0',
                berat_b: beratData.b || '0',
                berat_sb_a: beratData['sb-a'] || '0',
                berat_sb: beratData.sb || '0',
                
                // Data warna daun
                warna_lima: warnaDaunData.lima || '0',
                warna_empat: warnaDaunData.empat || '0',
                warna_tiga: warnaDaunData.tiga || '0',
                warna_dua: warnaDaunData.dua || '0',
                
                // Data tanaman abnormal
                abnormal_mb: tanamanAbnormalData.mb || '0',
                abnormal_phy: tanamanAbnormalData.phy || '0',
                abnormal_erw: tanamanAbnormalData.erw || '0',
                abnormal_lain_lain: totalLainLain.toString(),
                
                // Data buah kerdil
                buah_kerdil: kerdil
            });
        });
        
        // Urutkan plot berdasarkan nomor plot
        plotArray.sort((a, b) => {
            const plotNumA = parseInt(a.plot) || 0;
            const plotNumB = parseInt(b.plot) || 0;
            return plotNumA - plotNumB;
        });
        
        setPlotData(plotArray);
        
    }, [selectedLokasi, allData]);

    // Handle search - ketika memilih dari dropdown
    const handleSearch = (event, value) => {
        setSearchQuery(value || '');
        
        if (!value) {
            // Reset semua
            setSelectedLokasi(null);
            setPlotData([]);
            setLokasiDetail(null);
            return;
        }
        
        // Set lokasi yang dipilih
        setSelectedLokasi(value);
    };

    // Handle clear selection
    const handleClearSelection = () => {
        setSearchQuery('');
        setSelectedLokasi(null);
        setPlotData([]);
        setLokasiDetail(null);
    };

// Fungsi untuk export data ke Excel
const handleExport = () => {
    try {
        if (!selectedLokasi || plotData.length === 0) {
            setSnackbar({
                open: true,
                message: "Pilih lokasi terlebih dahulu dan pastikan ada data plot untuk export.",
                severity: "warning",
            });
            return;
        }
        
        // Buat data untuk export dengan struktur yang sesuai UI
        const exportData = [];
        
        // Header informasi lokasi
        exportData.push(['Lokasi', selectedLokasi]);
        exportData.push(['Tgl Peng.', lokasiDetail?.tanggal_pengamatan || '']);
        exportData.push(['FMP', lokasiDetail?.fmp || '']);
        exportData.push([]); // Baris kosong
        
        // Header pertama (row 4) - sesuai dengan UI
        const headerRow1 = [
            'Lokasi', 'Plot', 'PJP', // Lokasi, Plot, PJP (merge vertikal)
            // Sebaran Tanaman Sample (9 kolom) - merge horizontal
            'Sebaran Tanaman Sample', '', '', '', '', '', '', '', '',
            // Tanaman Abnormal (4 kolom) - merge horizontal
            'Tanaman Abnormal', '', '', '',
            // Total B.A - merge vertikal
            'Total B.A',
            // Buah Alami (5 kolom) - merge horizontal
            'Buah Alami', '', '', '', '',
            // Buah Kerdil - merge vertikal
            'Buah Kerdil',
            // Warna Daun (4 kolom) - merge horizontal
            'Warna Daun', '', '', '',
            // Berat Tanaman Destruktif (9 kolom) - merge horizontal
            'Berat Tanaman Destruktif (Kg)', '', '', '', '', '', '', '', ''
        ];
        
        // Header kedua (row 5) - sub-header
        const headerRow2 = [
            '', '', '', // Lokasi, Plot, PJP (kosong karena sudah di row 4)
            // Sub-header Sebaran Tanaman Sample
            'SK', 'SK-a', 'K', 'K-a', 'S', 'B-a', 'B', 'SB-a', 'SB',
            // Sub-header Tanaman Abnormal
            'MB', 'Phy', 'Erwinia', 'Lain-lain',
            // Total B.A (kosong karena sudah di row 4)
            '',
            // Sub-header Buah Alami
            '45-64', '65-85', '86-105', '106-125', '>125',
            // Buah Kerdil (kosong karena sudah di row 4)
            '',
            // Sub-header Warna Daun
            '5', '4', '3', '2',
            // Sub-header Berat Tanaman Destruktif
            'SK', 'SK-a', 'K', 'K-a', 'S', 'B-a', 'B', 'SB-a', 'SB'
        ];
        
        exportData.push(headerRow1);
        exportData.push(headerRow2);
        
        // Tambahkan data plot
        plotData.forEach(plot => {
            exportData.push([
                selectedLokasi,
                plot.plot || '-',
                plot.pjp || '-',
                // Sebaran Tanaman Sample
                plot.sebaran_sk || '0',
                plot.sebaran_sk_a || '0',
                plot.sebaran_k || '0',
                plot.sebaran_k_a || '0',
                plot.sebaran_s || '0',
                plot.sebaran_b_a || '0',
                plot.sebaran_b || '0',
                plot.sebaran_sb_a || '0',
                plot.sebaran_sb || '0',
                // Tanaman Abnormal
                plot.abnormal_mb || '0',
                plot.abnormal_phy || '0',
                plot.abnormal_erw || '0',
                plot.abnormal_lain_lain || '0',
                // Total B.A (placeholder)
                '-',
                // Buah Alami (placeholder)
                '-', '-', '-', '-', '-',
                // Buah Kerdil
                plot.buah_kerdil || '0',
                // Warna Daun
                plot.warna_lima || '0',
                plot.warna_empat || '0',
                plot.warna_tiga || '0',
                plot.warna_dua || '0',
                // Berat Tanaman Destruktif
                plot.berat_sk || '0',
                plot.berat_sk_a || '0',
                plot.berat_k || '0',
                plot.berat_k_a || '0',
                plot.berat_s || '0',
                plot.berat_b_a || '0',
                plot.berat_b || '0',
                plot.berat_sb_a || '0',
                plot.berat_sb || '0'
            ]);
        });
        
        // Buat worksheet
        const ws = XLSX.utils.aoa_to_sheet(exportData);
        
        // Definisikan rentang untuk merge cells sesuai struktur UI
        const merges = [
            // Merge untuk informasi lokasi (3 baris pertama - row 0-2)
            { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: 0 } },
            { s: { r: 2, c: 0 }, e: { r: 2, c: 0 } },
            
            // Merge untuk header tabel (mulai dari row 4)
            { s: { r: 4, c: 0 }, e: { r: 5, c: 0 } }, // Lokasi
            { s: { r: 4, c: 1 }, e: { r: 5, c: 1 } }, // plot
            { s: { r: 4, c: 2 }, e: { r: 5, c: 2 } }, // pjp
            
            // Sebaran Tanaman Sample
            { s: { r: 4, c: 3 }, e: { r: 4, c: 11 } },
            // Tanaman Abnormal
            { s: { r: 4, c: 12 }, e: { r: 4, c: 15 } },

            // Total B.A
            { s: { r: 4, c: 16 }, e: { r: 5, c: 16 } },

            // Buah Alami
            { s: { r: 4, c: 17 }, e: { r: 4, c: 21 } },

            // Buah Kerdil
            { s: { r: 4, c: 22 }, e: { r: 5, c: 22 } },

            // Warna Daun
            { s: { r: 4, c: 23 }, e: { r: 4, c: 26 } },
            // Berat Tanaman Destruktif
            { s: { r: 4, c: 27 }, e: { r: 4, c: 35 } }
        ];
        
        // Tambahkan merge settings ke worksheet
        ws['!merges'] = merges;
        
        // Atur lebar kolom
        const colWidths = [
            { wch: 10 }, // Lokasi
            { wch: 8 },  // Plot
            { wch: 8 },  // PJP
            // Sebaran Tanaman Sample (9 kolom)
            { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 6 }, 
            { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 6 },
            // Tanaman Abnormal (4 kolom)
            { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 10 },
            // Total B.A
            { wch: 10 },
            // Buah Alami (5 kolom)
            { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
            // Buah Kerdil
            { wch: 10 },
            // Warna Daun (4 kolom)
            { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 6 },
            // Berat Tanaman Destruktif (9 kolom)
            { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
            { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }
        ];
        
        ws['!cols'] = colWidths;
        
        // Styling untuk header
        const headerRows = [4, 5]; // Row index untuk header (0-based) - row 4, 5
        
        // Apply styling untuk semua cell
        const range = XLSX.utils.decode_range(ws['!ref']);
        
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell_address = { c: C, r: R };
                const cell_ref = XLSX.utils.encode_cell(cell_address);
                
                if (!ws[cell_ref]) continue;
                
                // Styling untuk header
                if (headerRows.includes(R)) {
                    ws[cell_ref].s = {
                        font: { bold: true },
                        alignment: { 
                            horizontal: 'center',
                            vertical: 'center',
                            wrapText: true
                        },
                        fill: {
                            fgColor: { rgb: "F0F0F0" } // Warna abu-abu muda
                        },
                        border: {
                            top: { style: 'thin', color: { rgb: "000000" } },
                            bottom: { style: 'thin', color: { rgb: "000000" } },
                            left: { style: 'thin', color: { rgb: "000000" } },
                            right: { style: 'thin', color: { rgb: "000000" } }
                        }
                    };
                } 
                // Styling untuk data (row 6 dan seterusnya)
                else if (R > headerRows[headerRows.length - 1]) {
                    ws[cell_ref].s = {
                        alignment: { 
                            horizontal: 'center',
                            vertical: 'center'
                        },
                        border: {
                            top: { style: 'thin', color: { rgb: "D0D0D0" } },
                            bottom: { style: 'thin', color: { rgb: "D0D0D0" } },
                            left: { style: 'thin', color: { rgb: "D0D0D0" } },
                            right: { style: 'thin', color: { rgb: "D0D0D0" } }
                        }
                    };
                }
                
                // Center alignment untuk semua cell
                if (!ws[cell_ref].s) {
                    ws[cell_ref].s = {
                        alignment: { 
                            horizontal: 'center',
                            vertical: 'center'
                        }
                    };
                } else if (!ws[cell_ref].s.alignment) {
                    ws[cell_ref].s.alignment = { 
                        horizontal: 'center',
                        vertical: 'center'
                    };
                }
            }
        }
        
        // Tambahkan catatan
        const noteRow = exportData.length;
        const noteCell = XLSX.utils.encode_cell({ r: noteRow, c: 0 });
        ws[noteCell] = { v: 'Catatan: Data buah alami akan ditambahkan setelah struktur data diketahui.' };
        ws[noteCell].s = {
            font: { italic: true },
            alignment: { horizontal: 'left' }
        };
        
        // Merge untuk catatan
        merges.push({ s: { r: noteRow, c: 0 }, e: { r: noteRow, c: 10 } });
        ws['!merges'] = merges;
        
        // Buat workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Data Plot");
        
        // Format tanggal pengamatan dari "DD-MM-YYYY" ke "DDMMYYYY"
        const formatTanggal = (tanggal) => {
            if (!tanggal) return '';
            // Ubah format dari "DD-MM-YYYY" ke "DDMMYYYY"
            return tanggal.replace(/-/g, '');
        };
        
        // Format tanggal pengamatan dan tanggal download
        const tanggalPengamatan = formatTanggal(lokasiDetail?.tanggal_pengamatan || '');
        const today = new Date();
        const tanggalDownload = `${String(today.getDate()).padStart(2, '0')}${String(today.getMonth() + 1).padStart(2, '0')}${today.getFullYear()}`;
        
        // Generate filename sesuai format yang diminta
        // Format: Peng. Berat Tanaman - [Lokasi] - [tanggal pengamatan DDMMYYYY] - [tanggal download DDMMYYYY]
        const filename = `Peng. Berat Tanaman - ${selectedLokasi} - ${tanggalPengamatan || 'NODATE'} - ${tanggalDownload}.xlsx`;
        
        // Export ke file
        XLSX.writeFile(wb, filename);
        
        setSnackbar({
            open: true,
            message: `Data plot untuk lokasi ${selectedLokasi} berhasil diexport ke Excel dengan struktur tabel lengkap.`,
            severity: "success",
        });
    } catch (error) {
        console.error("Error exporting data:", error);
        setSnackbar({
            open: true,
            message: "Gagal mengexport data.",
            severity: "error",
        });
    }
};

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Pengamatan Berat Tanaman SAP
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Autocomplete
                            freeSolo
                            options={searchOptions}
                            value={searchQuery}
                            onChange={handleSearch}
                            loading={loadingData}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Cari berdasarkan kode lokasi..."
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                        endAdornment: loadingData ? <CircularProgress size={20} /> : null
                                    }}
                                    sx={{ width: 300 }}
                                />
                            )}
                        />
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleExport}
                            disabled={!selectedLokasi || plotData.length === 0}
                        >
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Chip untuk menampilkan lokasi yang dipilih */}
                {selectedLokasi && (
                    <Box sx={{ mb: 2 }}>
                        <Chip
                            label={`Lokasi: ${selectedLokasi} - ${plotData.length} Plot`}
                            onDelete={handleClearSelection}
                            deleteIcon={<ClearIcon />}
                            color="primary"
                            variant="outlined"
                            sx={{ 
                                fontWeight: 'bold',
                                '& .MuiChip-deleteIcon': {
                                    color: 'primary.main'
                                }
                            }}
                        />
                    </Box>
                )}

                {loadingData ? (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            Memuat data pengamatan...
                        </Typography>
                    </Box>
                ) : selectedLokasi ? (
                    <>
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 400,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="pengamatan berat tanaman table">
                                <TableHead>
                                    <TableRow className="tr">
                                        <TableCell className="ded-tch" rowSpan={2} width="100">Lokasi</TableCell>
                                        <TableCell className="ded-tch" rowSpan={2} width="80">Plot</TableCell>
                                        <TableCell className="ded-tch" rowSpan={2} width="80">PJP</TableCell>
                                        
                                        {/* Sebaran Tanaman Sample */}
                                        <TableCell className="ded-tch" colSpan={9} align="center">
                                            Sebaran Tanaman Sample
                                        </TableCell>
                                        
                                        {/* Tanaman Abnormal */}
                                        <TableCell className="ded-tch" colSpan={4} align="center">
                                            Tanaman Abnormal
                                        </TableCell>
                                        
                                        {/* Total B.A */}
                                        <TableCell className="ded-tch" rowSpan={2} width="80">Total B.A</TableCell>
                                        
                                        {/* Buah Alami */}
                                        <TableCell className="ded-tch" colSpan={5} align="center">
                                            Buah Alami
                                        </TableCell>
                                        
                                        {/* Buah Kerdil */}
                                        <TableCell className="ded-tch" rowSpan={2} width="80">Buah Kerdil</TableCell>
                                        
                                        {/* Warna Daun */}
                                        <TableCell className="ded-tch" colSpan={4} align="center">
                                            Warna Daun
                                        </TableCell>
                                        
                                        {/* Berat Tanaman Destruktif */}
                                        <TableCell className="ded-tch" colSpan={9} align="center">
                                            Berat Tanaman Destruktif (Kg)
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Sub-header row */}
                                    <TableRow className="tr">
                                        
                                        {/* Sub-headers for Sebaran Tanaman Sample */}
                                        <TableCell className="ded-tc">SK</TableCell>
                                        <TableCell className="ded-tc">SK-a</TableCell>
                                        <TableCell className="ded-tc">K</TableCell>
                                        <TableCell className="ded-tc">K-a</TableCell>
                                        <TableCell className="ded-tc">S</TableCell>
                                        <TableCell className="ded-tc">B-a</TableCell>
                                        <TableCell className="ded-tc">B</TableCell>
                                        <TableCell className="ded-tc">SB-a</TableCell>
                                        <TableCell className="ded-tc">SB</TableCell>
                                        
                                        {/* Sub-headers for Tanaman Abnormal */}
                                        <TableCell className="ded-tc">MB</TableCell>
                                        <TableCell className="ded-tc">Phy</TableCell>
                                        <TableCell className="ded-tc">Erwinia</TableCell>
                                        <TableCell className="ded-tc">Lain-lain</TableCell>
                                        
                                        {/* Sub-headers for Buah Alami */}
                                        <TableCell className="ded-tc">45-64</TableCell>
                                        <TableCell className="ded-tc">65-85</TableCell>
                                        <TableCell className="ded-tc">86-105</TableCell>
                                        <TableCell className="ded-tc">106-125</TableCell>
                                        <TableCell className="ded-tc">&gt;125</TableCell>
                                        
                                        {/* Sub-headers for Warna Daun */}
                                        <TableCell className="ded-tc">5</TableCell>
                                        <TableCell className="ded-tc">4</TableCell>
                                        <TableCell className="ded-tc">3</TableCell>
                                        <TableCell className="ded-tc">2</TableCell>
                                        
                                        {/* Sub-headers for Berat Tanaman Destruktif */}
                                        <TableCell className="ded-tc">SK</TableCell>
                                        <TableCell className="ded-tc">SK-a</TableCell>
                                        <TableCell className="ded-tc">K</TableCell>
                                        <TableCell className="ded-tc">K-a</TableCell>
                                        <TableCell className="ded-tc">S</TableCell>
                                        <TableCell className="ded-tc">B-a</TableCell>
                                        <TableCell className="ded-tc">B</TableCell>
                                        <TableCell className="ded-tc">SB-a</TableCell>
                                        <TableCell className="ded-tc">SB</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {plotData.length > 0 ? (
                                        plotData.map((plot, index) => (
                                            <TableRow key={`${plot.plotKey}-${index}`}>
                                                {/* Kolom Lokasi - menampilkan kode lokasi */}
                                                <TableCell>{formatText(selectedLokasi)}</TableCell>
                                                
                                                {/* Kolom Plot - menampilkan data plot */}
                                                <TableCell>{plot.plot}</TableCell>
                                                
                                                {/* Kolom PJP - menampilkan data PJP */}
                                                <TableCell>{plot.pjp}</TableCell>
                                                
                                                {/* Sebaran Tanaman Sample (9 kolom) - data dari Firebase */}
                                                <TableCell>{plot.sebaran_sk}</TableCell>
                                                <TableCell>{plot.sebaran_sk_a}</TableCell>
                                                <TableCell>{plot.sebaran_k}</TableCell>
                                                <TableCell>{plot.sebaran_k_a}</TableCell>
                                                <TableCell>{plot.sebaran_s}</TableCell>
                                                <TableCell>{plot.sebaran_b_a}</TableCell>
                                                <TableCell>{plot.sebaran_b}</TableCell>
                                                <TableCell>{plot.sebaran_sb_a}</TableCell>
                                                <TableCell>{plot.sebaran_sb}</TableCell>
                                                
                                                {/* Tanaman Abnormal (4 kolom) - data dari Firebase */}
                                                <TableCell>{plot.abnormal_mb}</TableCell>
                                                <TableCell>{plot.abnormal_phy}</TableCell>
                                                <TableCell>{plot.abnormal_erw}</TableCell>
                                                <TableCell>{plot.abnormal_lain_lain}</TableCell>
                                                
                                                {/* Total B.A - placeholder */}
                                                <TableCell>-</TableCell>
                                                
                                                {/* Buah Alami (5 kolom) - placeholder */}
                                                <TableCell>-</TableCell>
                                                <TableCell>-</TableCell>
                                                <TableCell>-</TableCell>
                                                <TableCell>-</TableCell>
                                                <TableCell>-</TableCell>
                                                
                                                {/* Buah Kerdil - data dari Firebase */}
                                                <TableCell>{plot.buah_kerdil}</TableCell>
                                                
                                                {/* Warna Daun (4 kolom) - data dari Firebase */}
                                                <TableCell>{plot.warna_lima}</TableCell>
                                                <TableCell>{plot.warna_empat}</TableCell>
                                                <TableCell>{plot.warna_tiga}</TableCell>
                                                <TableCell>{plot.warna_dua}</TableCell>
                                                
                                                {/* Berat Tanaman Destruktif (9 kolom) - data dari Firebase */}
                                                <TableCell>{plot.berat_sk}</TableCell>
                                                <TableCell>{plot.berat_sk_a}</TableCell>
                                                <TableCell>{plot.berat_k}</TableCell>
                                                <TableCell>{plot.berat_k_a}</TableCell>
                                                <TableCell>{plot.berat_s}</TableCell>
                                                <TableCell>{plot.berat_b_a}</TableCell>
                                                <TableCell>{plot.berat_b}</TableCell>
                                                <TableCell>{plot.berat_sb_a}</TableCell>
                                                <TableCell>{plot.berat_sb}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={45} align="center" sx={{ py: 3 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {selectedLokasi ? 
                                                        `Tidak ada data plot untuk lokasi ${selectedLokasi}.` :
                                                        'Silakan pilih lokasi untuk menampilkan data.'
                                                    }
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Info jumlah data */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {lokasiDetail && (
                                    <>
                                        Tanggal Pengamatan: {lokasiDetail.tanggal_pengamatan}
                                        <br />
                                        FMP: {lokasiDetail.fmp}
                                    </>
                                )}
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchOptions.length > 0 ? 
                                'Silakan pilih kode lokasi dari dropdown pencarian untuk menampilkan data' :
                                'Tidak ada data pengamatan yang tersedia'
                            }
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
            
            {/* CSS untuk sub-header */}
            <style jsx="true">{`
                .sub-header {
                    font-size: 0.75rem;
                    font-weight: 500;
                    text-align: center;
                    padding: 4px 8px;
                    background-color: #f8f9fa;
                }
            `}</style>
        </Box>
    );
}

export default LayoutBeratTanamanSAP;